import React, { useState, useRef, RefObject, useLayoutEffect, ReactElement, useEffect, ReactNode } from 'react'
import {klass,TKlass,TStyle,Div, Span} from './html'
import ReactDOM from 'react-dom'
const L = console.log

const D = Div

type TMenuPos = {
  top: number
  left: number
  adjustments?: {
    top: number
    left: number
  }
}

export interface IMenuChoice {
  label: string | ReactElement
  el?: boolean
  choose?: (() => boolean) | (() => void)
  selected?: {
    k?: TKlass
    s?: TStyle
    label: string
  }
  disabled?: boolean
  divider?: 'top' | 'bottom'
  choices?: IMenuChoice[] | (() => IMenuChoice[])
  s?: TStyle
  k?: TKlass
}

////////////////////////////////////////////////////////////////////////////////
// NESTING MENU WIDGET
////////////////////////////////////////////////////////////////////////////////

export function MenuChoices ({
  k,
  items,
  sub,
  parent,
  close,
}: {
  k?: TKlass
  items: IMenuChoice[] | (() => IMenuChoice[])
  sub?: true
  parent?: {
    ref: RefObject<HTMLElement>
    pos?: () => TMenuPos
  }
  close: () => void
}) {
  const [position, setPosition] = useState<TMenuPos>()
  const ref = useRef<HTMLUListElement>(null)

  function pos(): TMenuPos {
    return position || { top: 0, left: 0 }
  }

  useLayoutEffect(() => {
    if (ref.current && parent?.ref.current) {
      const VIEWPORT_MARGIN = sub ? 0 : 8
      
      const parentRect = parent.ref.current.getBoundingClientRect()
      const menu = ref.current.getBoundingClientRect()
      const viewWidth = window.innerWidth - VIEWPORT_MARGIN
      const viewHeight = window.innerHeight - VIEWPORT_MARGIN
  
      const parentPos = parent.pos?.() ?? { top: 0, left: 0, adjustments: { top: 0, left: 0 } }
      const parentAdjustTop = parentPos.adjustments?.top ?? 0
      const parentAdjustLeft = parentPos.adjustments?.left ?? 0
  
      // Initial position relative to parent
      const left = sub ? parentRect.width * 0.9 : 0
      // For submenus, start at the top of the parent (don't compensate for parent's adjustment)
      const top = sub ? 0 : parentRect.height  
  
      // Calculate needed adjustments
      let topAdjust = 0
      let leftAdjust = 0
  
      // BOTTOM-SIDE clip?
      const menuTop = parentRect.top + top
      if (menuTop + menu.height > viewHeight) {
        topAdjust = viewHeight - (menuTop + menu.height)
      }
      // TOP-SIDE clip?
      if (menuTop + topAdjust < VIEWPORT_MARGIN) {
        topAdjust = VIEWPORT_MARGIN - menuTop
      }
  
      const menuLeft = parentRect.left + left + parentAdjustLeft
      if (menuLeft + menu.width + 180 > viewWidth) {
        const OVERLAP = 5
        leftAdjust = -(menu.width + (sub ? parentRect.width : 0) - OVERLAP)
      }
  
      setPosition({
        top: top + window.scrollY,
        left: left + window.scrollX,
        adjustments: {
          top: topAdjust,
          left: leftAdjust
        }
      })
    }
  }, [parent?.ref.current, parent?.pos, sub])

  return (
    <ul
        ref={ref}
        className={klass(k,sub ? 'submenu-list' : 'menu-list')}
        style={{
            position: 'absolute',
            top: position ? `${position.top + (position.adjustments?.top || 0)}px` : undefined,
            left: position ? `${position.left + (position.adjustments?.left || 0)}px` : undefined,
            zIndex: 1000,
        }}
    >
      {(Array.isArray(items) ? items : items()).map((item, index) => {
        return (
          <React.Fragment key={index}>
            <MenuItem
              item={item}
              key={index}
              parent={{
                ref,
                pos,
              }}
              close={close}
            />
          </React.Fragment>
        )
      })}
    </ul>
  )
}

export function MenuItem (
    {
        item,
        close,
        parent,
    }: {
        item: IMenuChoice
        close: () => void
        parent: {
            ref: RefObject<HTMLElement>
            pos?: () => TMenuPos
        }
    })
{
  const { choose, label, k, s, divider, selected, choices, disabled,el } = item
  const active = !!choose
  const ref = useRef<HTMLLIElement>(null)

  if (React.isValidElement(label) && !el) {
    return (
      <li className={klass(k)} style={s}>
        {label}
      </li>
    )
  }

  const hasChoices = choices?.length || choices instanceof Function

  return (
    <li
      ref={ref}
      className={
        klass(
            'menu-item',
            active && !disabled && 'active',
            choices && 'cascade',
            choices && !hasChoices && 'empty',
            selected && 'selected',
            disabled && 'disabled',
            divider && `div-${divider}`,
            k)
      }
      style={s}
      onClick={e => {
        if (choose && !disabled) {
          // If choose() returns false, keep the menu open
          // If choose() returns undefined/void/true, close the menu
          const shouldKeepOpen = choose() === false
          if (!shouldKeepOpen) {
            close()
          }
        }
        e.stopPropagation()
        e.preventDefault()
      }}
    >
      {
        React.isValidElement(label)
        ? label
        : <span className="label">{label}</span>
      }
      {selected?.label && (
        <span className={klass(selected.k)} style={selected.s}>
          {selected.label}
        </span>
      )}
      {hasChoices && (
        <MenuChoices
          sub={true}
          parent={{ ref, pos: parent.pos }}
          items={choices}
          close={close}
        />
      )}
    </li>
  )
}

// Popup Menu component
export function PopupMenu (
    { triggerRef, isVisible, close, items, k, offset={x:0,y:0}, onover}
    : {
        k?: any,
        triggerRef: React.RefObject<any>
        close: ()=>void
        offset?: {
          x?: number
          y?: number
        },
        isVisible: boolean
        items: IMenuChoice[] | (() => IMenuChoice[])
        onover?: () => void
    }
) 
{
    const [position, setPosition] = useState<{top:number,left:number}>()
    const ref = useRef<HTMLDivElement>(null)
    offset = offset || {}

    useEffect(() => {
      if (!ref.current || !onover) return
      ref.current.onmouseenter=onover
      return () => {
        if (ref.current) {
          ref.current.onmouseenter=null
        }
      }
    },[onover,ref])

    /* The menu portals to body and sits over its own trigger, so the pointer
       leaves whatever the trigger is nested in and :hover drops. Mark the
       trigger while the menu is open; ancestors that want to hold their hover
       key off it with :has([data-menu-open]). An attribute, not a class, so a
       re-render of the trigger can't wipe it. */
    useEffect(() => {
      const trigger = triggerRef.current
      if (!(trigger instanceof HTMLElement)) return
      trigger.toggleAttribute('data-menu-open',isVisible)
      return () => trigger.removeAttribute('data-menu-open')
    },[isVisible,triggerRef])

    useLayoutEffect(() => {
        if (isVisible && triggerRef.current && ref.current) {
            const parent = triggerRef.current.getBoundingClientRect()
            const menu = ref.current.getBoundingClientRect()
            const viewportWidth = window.innerWidth
            const viewportHeight = window.innerHeight
      
            const leftClip = parent.left + menu.width > viewportWidth
            // Check for right-side clipping and adjust
            const left = leftClip
              ? parent.right - menu.width + window.scrollX
              : parent.left + window.scrollX
      
            const botClip = parent.bottom + menu.height > viewportHeight
            // MODIFY unclipped position slightly to obscure menu trigger
            const obscureY = botClip ? 0 : -parent.height * 0.6
            // Check for bottom-side clipping and adjust
            const top = botClip
                ? parent.top - menu.height + window.scrollY
                : parent.top + window.scrollY + obscureY

            setPosition({ top, left })
          }
    }, [isVisible,triggerRef,ref])

    // Render the popup conditionally
    return isVisible
      ? ReactDOM.createPortal(
          <D
                ref={ref}
                k={[k,'popup-menu', k]}
                s={{
                    /* DO NOT DISPLAY ... until position has been set */
                    visibility: 'visible',
                    position: 'absolute',
                    top: position ? `${position.top + (offset.y ?? 0)}px` : undefined,
                    left: position ? `${position.left + (offset.x ?? 0)}px` : undefined,
                    height: '0px',
                    zIndex: 1000,
                }}
                onMouseLeave={close} // HIDE menu when cursor leaves
            >
            {
                ref.current
                && <MenuChoices items={items} parent={{ref: ref}} close={close}/>
            }
          </D>,
          document.body // RENDER popup into body to avoid any parent clipping
        )
      : null
 }

export function RolloverPop (
    { 
        items,
        triggerRef,
        disabled,
        id,
        k,
        s
    } :
    {
        id?: string
        triggerRef: React.RefObject<HTMLDivElement>
        disabled?: boolean
        items: (IMenuChoice|false|undefined)[] | (() => IMenuChoice[])
        k?: TKlass|TKlass[]
        s?: TStyle
    }
)
{
    const [isPopupVisible, setPopupVisible] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    const [entered,setEntered] = useState<boolean>()

    useEffect(() => {
      const ref = triggerRef.current
      if (!ref) return
      ref.onmouseenter=() => !disabled && setPopupVisible(true)
      //ref.onmouseleave=() => !disabled && !entered && setPopupVisible(false)
      return () => {
        ref.onmouseenter=null
        //ref.onmouseleave=null
      }
    },[triggerRef,entered])

    return (
        <D k='popup-trigger' id={id}>
            <D
                ref={ref}
                k={[k,'popup']}
                s={s}>
              <Span k={['symbols']}>arrow_forward_ios</Span>
              <PopupMenu
                  triggerRef={ref as React.RefObject<any>}
                  isVisible={isPopupVisible}
                  close={() => {
                    setPopupVisible(false)
                  }}
                  items={Array.isArray(items) ? items.filter(_ => _) as IMenuChoice[] : items}
              />
            </D>
        </D>
    )
}

export function TriPop(
    { 
        items,
        el,
        id,
        k,
        s,
        glyph = 'more_vert',
        offset = {x: 0,y:5},
    } :
    {
        el?: React.ReactNode
        id?: string
        items: (IMenuChoice|false|undefined)[] | (() => IMenuChoice[])
        k?: TKlass|TKlass[]
        s?: TStyle
        glyph?: string | React.ReactNode
        offset?: {x?: number, y?: number}
    }
)
{
    const [isPopupVisible, setPopupVisible] = useState(false)
    const ref = useRef(null)
  
    // SHOW/HIDE popup
    const togglePopup = () => setPopupVisible(prev => !prev)
  
    return (
            <D
                  id={id}
                  k={[k,'popup','acticon']}
                  ref={ref} 
                  onClick={togglePopup}
                  s={s}>
              <>
                {el && el }
                <Span k="tridot">{glyph}</Span>
              </>
              <PopupMenu
                  offset={offset}
                  triggerRef={ref as React.RefObject<any>}
                  isVisible={isPopupVisible}
                  close={() => setPopupVisible(false)}
                  items={Array.isArray(items) ? items.filter(_ => _) as IMenuChoice[] : items}
              />
            </D>
    )
}

export function ContextPop (
    { 
        items,
        id,
        k,
        s
    } :
    {
        id?: string
        items: (IMenuChoice|false|undefined)[] | (() => IMenuChoice[])
        k?: TKlass|TKlass[]
        s?: TStyle
    }
)
{
    const [isPopupVisible, setPopupVisible] = useState(false)
    const ref = useRef(null)
  
    // SHOW/HIDE popup
    const togglePopup = e => {
      e.preventDefault()
      setPopupVisible(prev => !prev)
    }
  
    return (
        <D k='popup-trigger' id={id}>
            <D
                  k={[k,'popup','acticon']}
                  ref={ref} 
                  onContextMenu={togglePopup}
                  s={{color: '#00000000',...s}}>
              <span className="symbols">more_vert</span>
              <PopupMenu
                  triggerRef={ref as React.RefObject<any>}
                  isVisible={isPopupVisible}
                  close={() => setPopupVisible(false)}
                  items={Array.isArray(items) ? items.filter(_ => _) as IMenuChoice[] : items}
              />
            </D>
        </D>
    )
}

/*
// CUSTOM HOOK for context menu functionality
export function ContextMenu (
    { pref, choices:$choices, k, s }
    : {
        pref: RefObject<HTMLDivElement>,
        choices: IMenuChoice[] | (() => IMenuChoice[])
        k?: TKlass|TKlass[]
        s?: TStyle
    })
{
  const [position, setPosition] = useState<{x: number,y: number}>()
  const menuRef = useRef<HTMLDivElement>(null)
  const [choices,setChoices] = useState<IMenuChoice[]>()

  useEffect(() => {
    const el = pref?.current
    if (el) {
      el.addEventListener('contextmenu', handleContextMenu)
      return () => el.removeEventListener('contextmenu', handleContextMenu)
    }
  }, [pref])

  const handleContextMenu = e => {
    e.preventDefault()
    setPosition({ x: e.clientX, y: e.clientY })
    if (!choices) {
      setChoices(Array.isArray($choices) ? $choices : $choices())
    }
  }

  if (position) {
    return ReactDOM.createPortal(
          <D
                ref={menuRef}
                k={[k,'popup-menu', k]}
                s={{
                    visibility: 'visible',
                    position: 'absolute',
                    top: position ? `${position.y - 50}px` : undefined,
                    left: position ? `${position.x - 50}px` : undefined,
                    height: '0px',
                    zIndex: 1000,
                }}
                onMouseLeave={() => setPosition(undefined)} // HIDE menu when cursor leaves
            >
            {
                menuRef?.current
                && <MenuChoices items={choices||[]} parent={{ref:pref}} close={() => setPosition(undefined)}/>
            }
          </D>,
          document.body // RENDER popup into body to avoid any parent clipping
        )
      }
}
*/
